var _excluded = ["children", "className", "disabledTabClassName", "domRef", "focus", "forceRenderTabPanel", "onSelect", "selectedIndex", "selectedTabClassName", "selectedTabPanelClassName", "environment", "disableUpDownKeys"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import PropTypes from 'prop-types';
import React, { cloneElement, useRef } from 'react';
import cx from 'clsx';
import uuid from '../helpers/uuid';
import { childrenPropType } from '../helpers/propTypes';
import { getTabsCount as getTabsCountHelper } from '../helpers/count';
import { deepMap } from '../helpers/childrenDeepMap';
import { isTabList, isTabPanel, isTab } from '../helpers/elementTypes';

function isNode(node) {
  return node && 'getAttribute' in node;
} // Determine if a node from event.target is a Tab element


function isTabNode(node) {
  return isNode(node) && node.getAttribute('data-rttab');
} // Determine if a tab node is disabled


function isTabDisabled(node) {
  return isNode(node) && node.getAttribute('aria-disabled') === 'true';
}

var canUseActiveElement;

function determineCanUseActiveElement(environment) {
  var env = environment || (typeof window !== 'undefined' ? window : undefined);

  try {
    canUseActiveElement = !!(typeof env !== 'undefined' && env.document && env.document.activeElement);
  } catch (e) {
    // Work around for IE bug when accessing document.activeElement in an iframe
    // Refer to the following resources:
    // http://stackoverflow.com/a/10982960/369687
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/12733599
    // istanbul ignore next
    canUseActiveElement = false;
  }
}

var defaultProps = {
  className: 'react-tabs',
  focus: false
};
var propTypes = process.env.NODE_ENV !== "production" ? {
  children: childrenPropType,
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  disabledTabClassName: PropTypes.string,
  disableUpDownKeys: PropTypes.bool,
  domRef: PropTypes.func,
  focus: PropTypes.bool,
  forceRenderTabPanel: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  selectedTabClassName: PropTypes.string,
  selectedTabPanelClassName: PropTypes.string,
  environment: PropTypes.object
} : {};

var UncontrolledTabs = function UncontrolledTabs(props) {
  var tabNodes = useRef([]);
  var tabIds = useRef([]);
  var panelIds = useRef([]);

  var _ref = useRef();

  function setSelected(index, event) {
    // Check index boundary
    if (index < 0 || index >= getTabsCount()) return;
    var onSelect = props.onSelect,
        selectedIndex = props.selectedIndex; // Call change event handler

    onSelect(index, selectedIndex, event);
  }

  function getNextTab(index) {
    var count = getTabsCount(); // Look for non-disabled tab from index to the last tab on the right

    for (var i = index + 1; i < count; i++) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    } // If no tab found, continue searching from first on left to index


    for (var _i = 0; _i < index; _i++) {
      if (!isTabDisabled(getTab(_i))) {
        return _i;
      }
    } // No tabs are disabled, return index


    return index;
  }

  function getPrevTab(index) {
    var i = index; // Look for non-disabled tab from index to first tab on the left

    while (i--) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    } // If no tab found, continue searching from last tab on right to index


    i = getTabsCount();

    while (i-- > index) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    } // No tabs are disabled, return index


    return index;
  }

  function getFirstTab() {
    var count = getTabsCount(); // Look for non disabled tab from the first tab

    for (var i = 0; i < count; i++) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    return null;
  }

  function getLastTab() {
    var i = getTabsCount(); // Look for non disabled tab from the last tab

    while (i--) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    return null;
  }

  function getTabsCount() {
    var children = props.children;
    return getTabsCountHelper(children);
  }

  function getTab(index) {
    return tabNodes.current["tabs-" + index];
  }

  function getChildren() {
    var index = 0;
    var children = props.children,
        disabledTabClassName = props.disabledTabClassName,
        focus = props.focus,
        forceRenderTabPanel = props.forceRenderTabPanel,
        selectedIndex = props.selectedIndex,
        selectedTabClassName = props.selectedTabClassName,
        selectedTabPanelClassName = props.selectedTabPanelClassName,
        environment = props.environment;
    tabIds.current = tabIds.current || [];
    panelIds.current = panelIds.current || [];
    var diff = tabIds.current.length - getTabsCount(); // Add ids if new tabs have been added
    // Don't bother removing ids, just keep them in case they are added again
    // This is more efficient, and keeps the uuid counter under control

    while (diff++ < 0) {
      tabIds.current.push(uuid());
      panelIds.current.push(uuid());
    } // Map children to dynamically setup refs


    return deepMap(children, function (child) {
      var result = child; // Clone TabList and Tab components to have refs

      if (isTabList(child)) {
        var listIndex = 0; // Figure out if the current focus in the DOM is set on a Tab
        // If it is we should keep the focus on the next selected tab

        var wasTabFocused = false;

        if (canUseActiveElement == null) {
          determineCanUseActiveElement(environment);
        }

        if (canUseActiveElement) {
          wasTabFocused = React.Children.toArray(child.props.children).filter(isTab).some(function (tab, i) {
            var env = environment || (typeof window !== 'undefined' ? window : undefined);
            return env && env.document.activeElement === getTab(i);
          });
        }

        result = /*#__PURE__*/cloneElement(child, {
          children: deepMap(child.props.children, function (tab) {
            var key = "tabs-" + listIndex;
            var selected = selectedIndex === listIndex;
            var props = {
              tabRef: function tabRef(node) {
                tabNodes.current[key] = node;
              },
              id: tabIds.current[listIndex],
              panelId: panelIds.current[listIndex],
              selected: selected,
              focus: selected && (focus || wasTabFocused)
            };
            if (selectedTabClassName) props.selectedClassName = selectedTabClassName;
            if (disabledTabClassName) props.disabledClassName = disabledTabClassName;
            listIndex++;
            return /*#__PURE__*/cloneElement(tab, props);
          })
        });
      } else if (isTabPanel(child)) {
        var _props = {
          id: panelIds.current[index],
          tabId: tabIds.current[index],
          selected: selectedIndex === index
        };
        if (forceRenderTabPanel) _props.forceRender = forceRenderTabPanel;
        if (selectedTabPanelClassName) _props.selectedClassName = selectedTabPanelClassName;
        index++;
        result = /*#__PURE__*/cloneElement(child, _props);
      }

      return result;
    });
  }

  function handleKeyDown(e) {
    var direction = props.direction,
        disableUpDownKeys = props.disableUpDownKeys;

    if (isTabFromContainer(e.target)) {
      var index = props.selectedIndex;
      var preventDefault = false;
      var useSelectedIndex = false;

      if (e.keyCode === 32
      /* space */
      || e.keyCode === 13
      /* enter */
      ) {
        preventDefault = true;
        useSelectedIndex = false;
        handleClick(e);
      }

      if (e.keyCode === 37
      /* arrow left */
      || !disableUpDownKeys && e.keyCode === 38
      /* arrow up */
      ) {
        // Select next tab to the left, validate if up arrow is not disabled
        if (direction === 'rtl') {
          index = getNextTab(index);
        } else {
          index = getPrevTab(index);
        }

        preventDefault = true;
        useSelectedIndex = true;
      } else if (e.keyCode === 39
      /* arrow right */
      || !disableUpDownKeys && e.keyCode === 40
      /* arrow down */
      ) {
        // Select next tab to the right, validate if down arrow is not disabled
        if (direction === 'rtl') {
          index = getPrevTab(index);
        } else {
          index = getNextTab(index);
        }

        preventDefault = true;
        useSelectedIndex = true;
      } else if (e.keyCode === 35) {
        // Select last tab (End key)
        index = getLastTab();
        preventDefault = true;
        useSelectedIndex = true;
      } else if (e.keyCode === 36) {
        // Select first tab (Home key)
        index = getFirstTab();
        preventDefault = true;
        useSelectedIndex = true;
      } // This prevents scrollbars from moving around


      if (preventDefault) {
        e.preventDefault();
      } // Only use the selected index in the state if we're not using the tabbed index


      if (useSelectedIndex) {
        setSelected(index, e);
      }
    }
  }

  function handleClick(e) {
    var node = e.target;

    do {
      if (isTabFromContainer(node)) {
        if (isTabDisabled(node)) {
          return;
        }

        var index = [].slice.call(node.parentNode.children).filter(isTabNode).indexOf(node);
        setSelected(index, e);
        return;
      }
    } while ((node = node.parentNode) != null);
  }
  /**
   * Determine if a node from event.target is a Tab element for the current Tabs container.
   * If the clicked element is not a Tab, it returns false.
   * If it finds another Tabs container between the Tab and `this`, it returns false.
   */


  function isTabFromContainer(node) {
    // return immediately if the clicked element is not a Tab.
    if (!isTabNode(node)) {
      return false;
    } // Check if the first occurrence of a Tabs container is `this` one.


    var nodeAncestor = node.parentElement;

    do {
      if (nodeAncestor === _ref.current) return true;
      if (nodeAncestor.getAttribute('data-rttabs')) break;
      nodeAncestor = nodeAncestor.parentElement;
    } while (nodeAncestor);

    return false;
  }

  var children = props.children,
      className = props.className,
      disabledTabClassName = props.disabledTabClassName,
      domRef = props.domRef,
      focus = props.focus,
      forceRenderTabPanel = props.forceRenderTabPanel,
      onSelect = props.onSelect,
      selectedIndex = props.selectedIndex,
      selectedTabClassName = props.selectedTabClassName,
      selectedTabPanelClassName = props.selectedTabPanelClassName,
      environment = props.environment,
      disableUpDownKeys = props.disableUpDownKeys,
      attributes = _objectWithoutPropertiesLoose(props, _excluded);

  return /*#__PURE__*/React.createElement("div", _extends({}, attributes, {
    className: cx(className),
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    ref: function ref(node) {
      _ref.current = node;
      if (domRef) domRef(node);
    },
    "data-rttabs": true
  }), getChildren());
};

UncontrolledTabs.defaultProps = defaultProps;
UncontrolledTabs.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
export default UncontrolledTabs;